import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';

import { Assignment } from '../../models/assignment.school.model';
import * as assignmentActions from '../actions/assignment.school.actions';

export interface AssignmentState extends EntityState<Assignment> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  assignmentStatsLoading: boolean;
  error: AppExceptionModel | undefined;
}

export const assignmentAdapter: EntityAdapter<Assignment> =
  createEntityAdapter<Assignment>();

export const initialState: AssignmentState = assignmentAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  assignmentStatsLoading: false,
  error: undefined,
});

export const schoolAssignmentReducer = createReducer(
  initialState,
  on(assignmentActions.getAssignments, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(assignmentActions.getAssignment, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(assignmentActions.addAssignmentToStore, (state, { assignment }) =>
    assignmentAdapter.upsertOne(assignment, {
      ...state,
      error: undefined,
      loading: false,
      loaded: true,
    }),
  ),
  on(assignmentActions.addAssignmentsToStore, (state, { assignments }) => {
    return assignmentAdapter.upsertMany(assignments, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      error: undefined,
    });
  }),
  on(assignmentActions.getAssignmentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(assignmentActions.getAssignmentFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(assignmentActions.create, (state, { assignment }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(assignmentActions.createSuccess, (state, { assignment }) => {
    return assignmentAdapter.upsertOne(assignment, {
      ...state,
      error: undefined,
      inProgress: false,
    });
  }),
  on(assignmentActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(assignmentActions.update, (state, { assignment }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(assignmentActions.updateSuccess, (state, { assignment }) =>
    assignmentAdapter.upsertOne(assignment, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(assignmentActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(assignmentActions.updateNotesSuccess, (state, { assignment }) =>
    assignmentAdapter.upsertOne(assignment, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(assignmentActions.updateNotesFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(assignmentActions.deleteAction, (state, { assignmentId: id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(assignmentActions.deleteSuccess, (state, { id }) =>
    assignmentAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(assignmentActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(assignmentActions.clearFromStore, (state, { ids }) =>
    assignmentAdapter.removeMany(ids, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(assignmentActions.clear, (state) => initialState),
);
